<template>
  <div class="mx-10 ma-5">
    <v-card class="pa-5">
      <v-row>
        <v-col cols="12" md="3">
          <v-select
            v-model="filters.branch_id"
            :items="branches"
            item-text="name"
            item-value="id"
            label="Branch"
            clearable
          />
        </v-col>
        <v-col cols="12" md="3">
          <v-menu
            ref="startMenu"
            v-model="startMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template #activator="{ on, attrs }">
              <v-text-field
                v-model="filters.start_date"
                label="Start Date"
                readonly
                v-bind="attrs"
                v-on="on"
              />
            </template>
            <v-date-picker
              v-model="filters.start_date"
              @input="startMenu = false"
            />
          </v-menu>
        </v-col>
        <v-col cols="12" md="3">
          <v-menu
            ref="endMenu"
            v-model="endMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template #activator="{ on, attrs }">
              <v-text-field
                v-model="filters.end_date"
                label="End Date"
                readonly
                v-bind="attrs"
                v-on="on"
              />
            </template>
            <v-date-picker
              v-model="filters.end_date"
              @input="endMenu = false"
            />
          </v-menu>
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            v-model="filters.type"
            :items="typeOptions"
            label="Report Type"
            item-text="label"
            item-value="value"
            required
          />
        </v-col>
        <v-col cols="12" md="3" v-if="filters.type === 'product'">
          <v-autocomplete
            v-model="selectedProduct"
            :items="productOptions"
            item-text="name"
            item-value="id"
            label="Select Product (optional)"
            clearable
            dense
            hide-details
            @update:search-input="fetchProducts"
          />
        </v-col>
        <v-col cols="12" md="3" v-if="filters.type === 'category'">
          <v-autocomplete
            v-model="selectedCategory"
            :items="categoryOptions"
            item-text="name"
            item-value="id"
            label="Select Category (optional)"
            clearable
            dense
            hide-details
            @update:search-input="fetchCategories"
          />
        </v-col>
        <v-col cols="12" md="2">
          <v-btn
            color="primary"
            @click="fetchReport"
            :loading="loading"
            :disabled="isGenerateDisabled"
            >Generate</v-btn
          >
        </v-col>
        <v-col cols="12" md="2">
          <v-btn
            color="success"
            @click="downloadPDF"
            :disabled="!reportData.length"
          >
            Download PDF
          </v-btn>
        </v-col>
      </v-row>
      <v-data-table
        :headers="headers"
        :items="reportData"
        :loading="loading"
        class="mt-5"
        :disable-pagination="true"
        :hide-default-footer="true"
      />
    </v-card>
  </div>
</template>

<script>
import { InvoiceService } from "@/services/invoice-service.js";
import { ReportService } from "@/services/report-service.js";
import { ProductService } from "@/services/product-service.js";
import { ProductCategoryService } from "@/services/productCategory-service.js";
import { mapGetters } from "vuex";
import jsPDF from "jspdf";
import "jspdf-autotable";

export default {
  name: "ProductSalesReport",
  components: {},
  data() {
    return {
      filters: {
        branch_id: null,
        start_date: null,
        end_date: null,
        type: "product",
      },
      typeOptions: [
        { label: "Product Wise", value: "product" },
        { label: "Category Wise", value: "category" },
      ],
      branches: [],
      reportData: [],
      loading: false,
      startMenu: false,
      endMenu: false,
      headers: [
        { text: "Description", value: "description" },
        { text: "Quantity", value: "quantity" },
        { text: "Total", value: "total" },
      ],
      selectedProduct: null,
      selectedCategory: null,
      productOptions: [],
      categoryOptions: [],
      productSearchTimeout: null,
      categorySearchTimeout: null,
    };
  },
  computed: {
    ...mapGetters("auth", ["hasPermission"]),
    isGenerateDisabled() {
      return (
        !this.filters.branch_id ||
        !this.filters.start_date ||
        !this.filters.end_date
      );
    },
  },
  created() {
    this.fetchBranches();
    this.fetchProducts();
    this.fetchReport();
  },
  watch: {
    "filters.branch_id"(val, oldVal) {
      if (val !== oldVal) this.fetchReport();
    },
    "filters.start_date"(val, oldVal) {
      if (val !== oldVal) this.fetchReport();
    },
    "filters.end_date"(val, oldVal) {
      if (val !== oldVal) this.fetchReport();
    },
    "filters.type"(val, oldVal) {
      if (val !== oldVal) this.fetchReport();
      this.selectedProduct = null;
      this.selectedCategory = null;
      if (val === "product") {
        this.fetchProducts();
      } else if (val === "category") {
        this.fetchCategories();
      }
    },
    selectedProduct(val, oldVal) {
      if (this.filters.type === "product" && val !== oldVal) this.fetchReport();
    },
    selectedCategory(val, oldVal) {
      if (this.filters.type === "category" && val !== oldVal)
        this.fetchReport();
    },
  },
  methods: {
    async fetchBranches() {
      try {
        const res = await InvoiceService.getBranches();
        this.branches = res.data || [];
      } catch (e) {
        this.branches = [];
      }
    },
    fetchProducts(search = "") {
      clearTimeout(this.productSearchTimeout);
      this.productSearchTimeout = setTimeout(async () => {
        try {
          const res = await ProductService.getProductList(search, 1);
          this.productOptions = res.data.data || [];
        } catch {
          this.productOptions = [];
        }
      }, 400);
    },
    fetchCategories(search = "") {
      clearTimeout(this.categorySearchTimeout);
      this.categorySearchTimeout = setTimeout(async () => {
        try {
          const res = await ProductCategoryService.getCategoryList(search, 1);
          this.categoryOptions = res.data.data || [];
        } catch {
          this.categoryOptions = [];
        }
      }, 400);
    },
    async fetchReport() {
      this.loading = true;
      try {
        const params = { ...this.filters };
        if (this.filters.type === "product" && this.selectedProduct) {
          params.product_id = this.selectedProduct;
        }
        if (this.filters.type === "category" && this.selectedCategory) {
          params.category_id = this.selectedCategory;
        }
        this.reportData = await ReportService.getSalesReport(params);
      } catch (e) {
        this.reportData = [];
      } finally {
        this.loading = false;
      }
    },
    downloadPDF() {
      const doc = new jsPDF();

      // Main heading: larger and bold
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("Product Sales Report", 14, 15);

      // Reset font for filters
      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");

      // Prepare filter summary, each on its own line
      let y = 22;
      const branch = this.branches.find(
        (b) => String(b.id) === String(this.filters.branch_id)
      );
      if (branch) {
        doc.text(`Branch: ${branch.name}`, 14, y);
        y += 7;
      }
      if (this.filters.start_date) {
        doc.text(`Start Date: ${this.filters.start_date}`, 14, y);
        y += 7;
      }
      if (this.filters.end_date) {
        doc.text(`End Date: ${this.filters.end_date}`, 14, y);
        y += 7;
      }
      if (this.filters.type === "product" && this.selectedProduct) {
        const product = this.productOptions.find(
          (p) => String(p.id) === String(this.selectedProduct)
        );
        if (product) {
          doc.text(`Product: ${product.name}`, 14, y);
          y += 7;
        }
      }
      if (this.filters.type === "category" && this.selectedCategory) {
        const category = this.categoryOptions.find(
          (c) => String(c.id) === String(this.selectedCategory)
        );
        if (category) {
          doc.text(`Category: ${category.name}`, 14, y);
          y += 7;
        }
      }
      // Add a little space before the table
      if (y > 22) y += 2;

      doc.autoTable({
        head: [this.headers.map((h) => h.text)],
        body: this.reportData.map((row) => [
          row.description,
          row.quantity,
          row.total,
        ]),
        startY: y,
      });

      doc.save("product_sales_report.pdf");
    },
  },
};
</script>
